import dayjs from 'dayjs';

import cookie from '../cookie';
import { getCurrentDomain } from '../host';

const DEFAULT_LIFE_TIME_MS = 24 * 60 * 60 * 1000;

/**
 * A class to manage saving and retrieving search parameters across subdomains
 * using cookies and localStorage with custom lifetime.
 */
export class SearchParamSaver {
  /**
   * Creates a new instance of SearchParamSaver.
   *
   * @constructor
   * @param {Object} options - Configuration options for the saver instance.
   * @param {string} [options.name] - The name used in localStorage and cookie.
   * @param {Array<string>} [options.searchKeys=[]] - A list of keys to track as search parameters.
   * @param {number} [options.lifetimeMs] - The duration (in milliseconds) for which the saved parameters will be available.
   * @param {Function} [options.cookieCondition] - A function that determines whether the parameters should be stored in cookies.
   * @param {OverrideOptions} [options.override] - Values which override original values from search params.
   * @param {boolean} [options.allowCookie=false] - Allow saving in cookie.
   */
  /**
   * Configuration options for overriding search parameters.
   *
   * @typedef {Object} OverrideOptions
   * @property {boolean} [force=false] - Determines if the override should take precedence over existing values.
   * @property {Function} value - A function returning the overridden value.
   */
  constructor(options) {
    this._name = options?.name || '';
    this._searchKeys = options?.searchKeys || [];
    this._lifetimeMs = options?.lifetimeMs || DEFAULT_LIFE_TIME_MS;
    this._cookieCondition = options?.cookieCondition || (() => true);
    this._overrideValues = options?.override || {};
    this._allowCookie =
      typeof options.allowCookie === 'boolean' ? options.allowCookie : false;
  }

  /**
   * Initializes the SearchParamSaver instance.
   *
   * Call this method after page loading.
   *
   * @returns {void}
   */
  init() {
    if (!this._name) return;

    this._checkUrl();
    this._scheduleCleaning();

    window.addEventListener(
      'cc:onSaveConsent',
      this._onCookieConsent.bind(this)
    );
  }

  /**
   * Retrieves the saved search parameters.
   *
   * @returns {Object|null} - The saved search parameters or null.
   */
  getParams() {
    const paramsFromCookie = this._readFromCookie();
    const paramsFromLocalStorage = this._readFromLocalStorage()?.params;

    return paramsFromCookie || paramsFromLocalStorage;
  }

  _checkUrl() {
    const paramsFromUrl = this._getParamsFromUrl();

    const hasParamsValues = Object.keys(paramsFromUrl).length;
    const hasOverrideValues = Object.keys(this._overrideValues).length;

    if (!hasParamsValues && !hasOverrideValues) return;

    const result = { ...paramsFromUrl };

    for (const key of this._searchKeys) {
      if (!this._overrideValues[key]) continue;

      const originalValue = paramsFromUrl[key];
      const overrideValue = this._overrideValues[key]?.value();
      const needToForceRedefine = this._overrideValues[key]?.force;

      if (!overrideValue || (originalValue && !needToForceRedefine)) continue;

      result[key] = overrideValue || null;
    }

    if (!Object.keys(result).length) return;

    this._writeToLocalStorage(result);
    this._writeToCookie();
  }

  _getParamsFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParams = {};

    this._searchKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        searchParams[key] = value;
      }
    });

    return searchParams;
  }

  _readFromLocalStorage() {
    const searchParams = localStorage.getItem(this._name);

    if (!searchParams) {
      this._writeToLocalStorage();
      return this._readFromLocalStorage();
    }

    return JSON.parse(searchParams);
  }

  _writeToLocalStorage(params = {}) {
    if (!Object.keys(params).length) {
      localStorage.setItem(
        this._name,
        JSON.stringify({
          expires: null,
          params: null,
        })
      );
      return;
    }

    const expires = dayjs().add(this._lifetimeMs, 'ms').toISOString();

    localStorage.setItem(this._name, JSON.stringify({ expires, params }));
  }

  _readFromCookie() {
    if (!this._cookieCondition()) return null;

    const cookieValue = cookie.get(this._name);

    if (!cookieValue) return null;

    return JSON.parse(cookieValue);
  }

  _writeToCookie() {
    if (!this._allowCookie) return;

    const domain = `.${getCurrentDomain()}`;

    if (!this._cookieCondition()) {
      return cookie.remove(this._name, { domain });
    }

    const data = this._readFromLocalStorage();

    if (!data?.expires || !data?.params) return;

    cookie.set(this._name, JSON.stringify(data.params), {
      domain,
      expires: dayjs(data.expires).toDate(),
    });
  }

  _scheduleCleaning() {
    this._clearCleanTimeout();

    const { expires } = this._readFromLocalStorage();

    if (!expires) return;

    const expiredDate = new Date(expires);
    const now = new Date();
    const isExpired = now > expiredDate;

    if (isExpired) return this._clear();

    const cleanTimeoutMs = expiredDate - now;

    this._cleanTimeout = setTimeout(this._clear.bind(this), cleanTimeoutMs);
  }

  _clear() {
    this._writeToLocalStorage();
  }

  _clearCleanTimeout() {
    if (!this._cleanTimeout) return;

    clearTimeout(this._cleanTimeout);
    this._cleanTimeout = null;
  }

  _onCookieConsent() {
    this._writeToCookie();
  }
}
